import { Injectable } from '@angular/core';
import { IBrandingService, IBrandingConfig } from '@smsamiinc/invoice'; // Can import from any plugin
import { BehaviorSubject, Observable } from 'rxjs';

import { environment } from '../../../environments/environment';

/**
 * Material Design branding service implementation
 * Uses Material Design 3 CSS custom properties for theming
 *
 * Configuration flows from environment file:
 * - App Logo: environment.orgLogo
 * - App Name: "ERetail Point" (hardcoded brand name)
 * - Default Thumbnail: environment.defualtThumbnail (for missing images)
 * - Theme colors: Material Design defaults
 */
@Injectable({ providedIn: 'root' })
export class MaterialBrandingService implements IBrandingService {
    private themeChange$ = new BehaviorSubject<IBrandingConfig>(this.getDefaultBranding());

    /**
     * Get the default branding configuration from environment
     */
    private getDefaultBranding(): IBrandingConfig {
        return {
            logoUrl: environment.orgLogo || '/assets/images/logo.png',
            appName: 'ERetail Point',
            themeMode: 'light',
            primaryColor: '#1976d2',
            secondaryColor: '#dc004e',
        };
    }

    /**
     * Get default thumbnail URL for missing images
     */
    getDefaultThumbnail(): string {
        return environment.defualtThumbnail || '/assets/images/default-img.jpg';
    }

    getLogoUrl(): string {
        return this.themeChange$.value.logoUrl;
    }

    getAppName(): string {
        return this.themeChange$.value.appName;
    }

    getThemeMode(): 'light' | 'dark' {
        return this.themeChange$.value.themeMode;
    }

    getPrimaryColor(): string {
        return this.themeChange$.value.primaryColor;
    }

    getSecondaryColor(): string {
        return this.themeChange$.value.secondaryColor;
    }

    /**
     * Set theme configuration and update Material Design CSS variables
     */
    setTheme(config: Partial<IBrandingConfig>): void {
        const newConfig = { ...this.themeChange$.value, ...config };
        this.themeChange$.next(newConfig);

        // Update Material Design CSS variables
        const root = document.documentElement;

        if (config.themeMode) {
            // Apply Material theme mode class
            document.body.classList.remove('light-theme', 'dark-theme');
            document.body.classList.add(`${config.themeMode}-theme`);
        }

        if (config.primaryColor) {
            root.style.setProperty('--mat-app-primary', config.primaryColor);
        }

        if (config.secondaryColor) {
            root.style.setProperty('--mat-app-secondary', config.secondaryColor);
        }
    }

    onThemeChange$(): Observable<IBrandingConfig> {
        return this.themeChange$.asObservable();
    }
}
